import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import theme from "@monorepo/styles/theme";
import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, Box, Button, Fade, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { IS_THIRD_PARTY } from "../../../constants/app";

interface IModalProps {
  confirmButtonText?: string;
  declineButtonText?: string;
  open: boolean;
  onAction?: () => void;
  onClose: () => void;
  size?: "small" | "medium" | "large";
  textAlign?: "center" | "left" | "right";
  unclosable?: boolean;
  showDefaultConfirmButton?: boolean;
}

const sizeClasses = {
  small: "360px",
  medium: "500px",
  large: "900px",
};

export const StyledModal = styled(Box, { shouldForwardProp: (prop) => prop !== "size" })<{
  size: "small" | "medium" | "large";
}>(({ size }) => ({
  width: "95vw",
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  overflowY: "auto",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "81%",
  padding: theme.spacing(4, 2),

  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(4 / 8),
  zIndex: 99999,
  background: theme.palette.background.paper,
  [theme.breakpoints.down("md")]: {
    maxWidth: sizeClasses[size],
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: sizeClasses[size],
  },
}));

export const Modal: React.FC<IModalProps> = ({
  confirmButtonText,
  declineButtonText,
  open,
  onAction,
  onClose,
  children,
  size = "small",
  textAlign,
  unclosable,
  showDefaultConfirmButton = true,
}) => {
  const { t } = useTranslation();
  const node = document.getElementById("modal-root") || document.createElement("div");

  return ReactDOM.createPortal(
    <>
      <Backdrop
        sx={{
          zIndex: 1000,
          ...(IS_THIRD_PARTY && {
            backgroundColor: "transparent",
          }),
        }}
        open={open}
        onClick={() => {
          onClose();
        }}
      >
        <Fade in={open}>
          <StyledModal
            size={size}
            sx={{ zIndex: 1001 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {!unclosable && (
              <IconButton
                aria-label={t("aria.close_modal")}
                sx={{ alignSelf: "flex-end" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: theme.spacing(0, 1),
                }}
              >
                {React.Children.map(children, (child, index) => {
                  const isString = typeof child === "string";
                  return (
                    <Box
                      key={String(index) + String(child)}
                      sx={{
                        margin:
                          index + 1 === React.Children.count(children)
                            ? `${theme.spacing(2)} 0px ${theme.spacing(3)}`
                            : `${theme.spacing(2, 0)}`,
                        textAlign: textAlign ?? "left",
                      }}
                    >
                      {isString ? <Typography>{child}</Typography> : child}
                    </Box>
                  );
                })}
                {onAction && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column-reverse",
                      [theme.breakpoints.up("md")]: {
                        flexDirection: "row",
                      },
                      columnGap: theme.spacing(2),
                      rowGap: theme.spacing(1),
                      flex: 1,
                    }}
                  >
                    <Button
                      disableElevation
                      variant="text"
                      sx={{
                        flex: 1,
                        minWidth: 170,
                        borderRadius: theme.spacing(4 / 8),
                        padding: "20px 15px",
                        textTransform: "capitalize",
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.primary.main,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                      }}
                    >
                      {declineButtonText ?? t("common.no")}
                    </Button>
                    <Button
                      disableElevation
                      sx={{
                        flex: 1,
                        minWidth: 170,
                        borderRadius: theme.spacing(4 / 8),
                        padding: "20px 15px",
                        textTransform: "capitalize",
                        backgroundColor: theme.palette.primary.main,
                        "&:hover, &:focus-visible": {
                          backgroundColor: theme.palette.primary.main,
                          opacity: 0.8,
                        },
                      }}
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction();
                      }}
                    >
                      {confirmButtonText ?? t("common.yes")}
                    </Button>
                  </Box>
                )}
                {showDefaultConfirmButton && !onAction && !unclosable && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      sx={{
                        minWidth: 170,
                        borderRadius: theme.spacing(4 / 8),
                        padding: "20px 15px",
                        textTransform: "capitalize",
                        backgroundColor: theme.palette.primary.main,
                        "&:hover, &:focus-visible": {
                          backgroundColor: theme.palette.primary.main,
                          opacity: 0.8,
                        },
                      }}
                      disableElevation
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                      }}
                    >
                      {confirmButtonText ?? t("common.ok")}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </StyledModal>
        </Fade>
      </Backdrop>
    </>,
    node
  );
};
